import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { adminApi, venueApi } from "../../../api/resources";
import type { EmployeePublic, ShiftQuery, VenueAdmin } from "../../../api/types";
import { SelectFilter } from "../../../components/Overlay";
import { TextInput } from "../../../components/ui";

const STATUS: [string, string][] = [["", "All statuses"], ["TENTATIVE", "Tentative"], ["CONFIRMED", "Confirmed"], ["CANCELLED", "Cancelled"]];
const SCOPE_LABEL: Record<string, string> = { next7: "Next 7 days", upcoming: "Upcoming", past: "Past" };

interface Props {
  value: ShiftQuery;
  onChange: (q: ShiftQuery) => void;
  showDates?: boolean;
}

export function ShiftFilters({ value, onChange, showDates = true }: Props) {
  const [venues, setVenues] = useState<VenueAdmin[]>([]);
  const [emps, setEmps] = useState<EmployeePublic[]>([]);
  useEffect(() => {
    venueApi.list(undefined, "ALL").then(setVenues).catch(() => setVenues([]));
    adminApi.employees().then(setEmps).catch(() => setEmps([]));
  }, []);
  const set = (k: keyof ShiftQuery, v: string | boolean) => onChange({ ...value, [k]: v || undefined });

  return (
    <div data-testid="shift-filters" className="mb-6 flex flex-wrap items-end gap-3">
      {showDates && (
        <>
          <label className="space-y-1">
            <span className="label-caps block text-zinc-500">From</span>
            <TextInput type="date" value={value.date_from ?? ""} onChange={(e) => set("date_from", e.target.value)} className="w-44 [color-scheme:dark]" data-testid="shift-filter-date-from" />
          </label>
          <label className="space-y-1">
            <span className="label-caps block text-zinc-500">To</span>
            <TextInput type="date" value={value.date_to ?? ""} onChange={(e) => set("date_to", e.target.value)} className="w-44 [color-scheme:dark]" data-testid="shift-filter-date-to" />
          </label>
        </>
      )}
      <SelectFilter label="Venue" testId="shift-filter-venue" value={value.venue_id ?? ""} onChange={(v) => set("venue_id", v)}
        options={[["", "All venues"], ...venues.map((v): [string, string] => [v.id, `${v.name}${v.status === "ARCHIVED" ? " (archived)" : ""}`])]} />
      <SelectFilter label="Employee" testId="shift-filter-employee" value={value.employee_id ?? ""} onChange={(v) => set("employee_id", v)}
        options={[["", "All employees"], ...emps.map((e): [string, string] => [e.id, e.full_name])]} />
      <SelectFilter label="Status" testId="shift-filter-status" value={value.status ?? ""} onChange={(v) => set("status", v)} options={STATUS} />
      <label className="flex h-11 cursor-pointer items-center gap-2 rounded-sm border border-white/15 px-3 text-sm text-zinc-300 hover:border-white/40">
        <input type="checkbox" checked={!!value.needs_attention} onChange={(e) => set("needs_attention", e.target.checked)} className="accent-signal" data-testid="shift-filter-needs-attention" />
        Needs attention
      </label>
      {value.scope && (
        <button type="button" onClick={() => set("scope", "")} data-testid="shift-filter-scope-chip"
          className="inline-flex h-11 items-center gap-2 rounded-sm border border-signal/50 bg-signal/10 px-3 text-sm text-white hover:bg-signal/20">
          {SCOPE_LABEL[value.scope] ?? value.scope} <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
